import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl, MessageDescriptor, FormattedMessage } from 'react-intl';
import { Field, FieldProps, useField } from 'formik';
import classNames from 'classnames';
import ErrorMessage from 'hellospa/components/error-message';

import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import TextArea from 'hellospa/foundation/control/textarea';
import TextField from 'hellospa/foundation/control/text-field';
import TooltipUI, {
  ArrowDirection,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';

import messages from './intl';
import styles from './index.scss';
import HelpIcon from './icons/help';
import HfReactHelper from 'common/utils/hf-react-helper';
import { SubsectionHeader, SectionHeader } from '../header';
import { includes } from 'hellospa/js-utils';

type Props = {
  useHeader?: MessageDescriptor;
};

const SenderUserInfoFields = () => {
  const intl = useIntl();
  const [, firstNameMessageMeta] = useField<String>('document.firstname');
  const [, lastNameMessageMeta] = useField<String>('document.lastname');

  return (
    <div className={classNames(styles.firstFieldsSet, styles.fields, styles.padding)}>
      <div
        className={classNames({
          [styles.errorFieldContainer]:
          (firstNameMessageMeta.touched && firstNameMessageMeta.error)
          || (lastNameMessageMeta.touched && lastNameMessageMeta.error),
        })}
      >
        <label htmlFor="document.firstname">
          <FormattedMessage
            id=""
            description="label text, identifies who is sending signature request"
            defaultMessage="From" />
          <div className={styles.infoIcon}>
            <HelpIcon />
            <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
              {intl.formatMessage(messages.fromToolTip)}
            </TooltipUI>
          </div>
        </label>
        <div className={styles.fieldRow}>
          <div className={styles.nameContainer}>
            <div className={styles.firstname}>
              <Field
                name="document.firstname"
              >
                {({ field }: FieldProps) => <TextField
                  id="document.firstname"
                  role="document.firstname"
                  placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                  data-qa-ref="document.fields.firstname"
                  {...field} />}
              </Field>
            </div>
          </div>
          <div className={styles.nameContainer}>
            <div className={styles.lastname}>
              <Field
                name="document.lastname"
              >
                {({ field }: FieldProps) => <TextField
                  id="document.lastname"
                  role="document.lastname"
                  placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                  data-qa-ref="document.fields.lastname"
                  {...field} />}
              </Field>
              <div
                className={styles.errorContainer}
                role="title-error"
                data-qa-ref="document.errors.lastname"
              >
              </div>
            </div>
          </div>
        </div>
        <div
          className={styles.errorContainer}
          role="title-error"
          data-qa-ref="document.errors.firstname"
        >
          {/* only shows one error for two fields */}
          {(firstNameMessageMeta.error) ? (
            <ErrorMessage
              name="document.firstname"
              className={styles.error} />
          ) : (
            <ErrorMessage
              name="document.lastname"
              className={styles.error} />
          )}
        </div>
      </div>
    </div>
  );
};

const DocumentSection = (({ useHeader }: Props) => {
  const intl = useIntl();
  const workflow = useSelector(selectors.getWorkflow);
  const allowDocumentMessage = useSelector(selectors.allowDocumentMessage);
  const documentTitleLabel = useSelector(selectors.getDocumentTitleLabel);
  const isDocumentMessageOptional = useSelector(selectors.isDocumentMessageOptional);
  const { lockDocumentMessage, lockDocumentTitle } = useSelector(selectors.getFlags);
  const isMeOnly = useSelector(selectors.isMeOnly);
  const userName = useSelector(selectors.getInitialValueOfNameFields);
  const isSignerInfoRequired = useSelector(selectors.isSignerInfoRequired);
  const isHelloFax = useSelector(selectors.isHelloFax);
  const hasHipaa = useSelector(selectors.hasHipaa);

  const [, documentTitleMeta] = useField<String>('document.title');
  const [, documentMessageMeta] = useField<String>('document.message');

  const settingUrl = HfReactHelper.urlHelper('home/myAccount#profile', 'hellosign');

  const header = React.useMemo(() => {
    if (useHeader) {
      return useHeader;
    }

    if (isMeOnly) {
      return null;
    }

    if (includes(workflow.tags, [
      types.WorkflowTags.SignatureRequestDocument,
      types.WorkflowTags.SignatureRequestTemplate,
      types.WorkflowTags.BulkSend,
    ])) {
      return messages.headerDefaultDocumentSection;
    }

    if (workflow.tags.includes(types.WorkflowTags.CreateTemplate)) {
      return messages.headerTemplateDocumentSection;
    }

    if (workflow.tags.includes(types.WorkflowTags.CreateTemplateLink)) {
      return messages.headerTemplateLinkDocumentSection;
    }

    return null;
  }, [useHeader, workflow, isMeOnly]);

  const documentMessageLabel = isDocumentMessageOptional
    ? messages.fieldMessageOptionalLabel
    : messages.fieldMessageLabel;

  const isSubheader = header === messages.headerFaxDocument;

  return (
    <div
      data-qa-ref="document.fields"
      className={styles.documentFields}
    >
      {header && (isSubheader
        ? <SubsectionHeader>{intl.formatMessage(header)}</SubsectionHeader>
        : <SectionHeader>{intl.formatMessage(header)}</SectionHeader>)}
      {!isHelloFax && userName && isSignerInfoRequired && <SenderUserInfoFields />}
      <div className={styles.fields}>
        <div className={styles.fieldRow}>
          <div
            className={classNames({
              [styles.errorFieldContainer]: documentTitleMeta.touched && documentTitleMeta.error,
            })}
          >
            <label htmlFor="document.title">
              {intl.formatMessage(documentTitleLabel)}
            </label>
            <Field
              name="document.title"
            >
              {({ field }: FieldProps) => <TextField
                id="document.title"
                disabled={lockDocumentTitle}
                data-qa-ref="document.fields.title"
                {...field} />}
            </Field>
            <div
              className={styles.errorContainer}
              role="title-error"
              data-qa-ref="document.errors.title"
            >
              <ErrorMessage
                name="document.title"
                className={styles.error} />
            </div>
          </div>
        </div>
      </div>
      {allowDocumentMessage && (
        <div className={styles.fields}>
          <div className={styles.fieldRow}>
            <div
              className={classNames({
                [styles.errorFieldContainer]:
                  documentMessageMeta.touched && documentMessageMeta.error,
              })}
            >
              <label htmlFor="document.message">
                {intl.formatMessage(documentMessageLabel)}
              </label>
              <Field
                name="document.message"
              >
                {({ field }: FieldProps) => <TextArea
                  id="document.message"
                  rows={4}
                  disabled={lockDocumentMessage}
                  data-qa-ref="document.fields.message"
                  {...field} />}
              </Field>
              <div
                className={styles.errorContainer}
                role="message-error"
                data-qa-ref="document.errors.message"
              >
                <ErrorMessage
                  name="document.message"
                  className={styles.error} />
              </div>
            </div>
          </div>
        </div>
      )}
      { !isHelloFax && isSignerInfoRequired && !hasHipaa
      && <div className={classNames(styles.fields, styles.margin)}>
        <p>
          <FormattedMessage
            id=""
            description="message in review page, informing sender can make adjustment from settings page about who receives the completed document"
            defaultMessage="All recipients will receive an email attachment of the completed documents once{br} everyone has signed. You can always edit these options in <a>settings</a>."
            values={{
            // eslint-disable-next-line react/display-name
              a: (...chunks: any[]) => <a href={settingUrl} target="_blank" rel="noopener noreferrer">{chunks}</a>,
              br: <br />,
            }} />
        </p>
        </div>
      }
    </div>
  );
});

export default DocumentSection;
